import { obtenirUrlControleur } from '../utils/urlUtils';
import Membre from '../modeles/Membre';
import dayjs from 'dayjs';
import { List } from 'immutable';
import fetch, { jsonHeaders } from '../utils/fetch';
import { MembreDto } from '../modeles/MembreDto';
import { ReponseMembreDto } from '../modeles/ReponseMembreDto';

const lienMembre = obtenirUrlControleur('membre');

const normaliserMembre = (membre: ReponseMembreDto) => {
    return new Membre({
        id: membre.id,
        nom: membre.nom,
        prenom: membre.prenom,
        structure: membre.structure,
        idStructure: '',
        dateInscription: membre.dateInscription ? dayjs(membre.dateInscription).toISOString() : undefined,
    });
};

function normaliserPayloadMembre(membre: Record<string, unknown>): MembreDto {
    return {
        id: String(membre?.id ?? membre?.Id ?? ''),
        nom: String(membre?.nom ?? membre?.Nom ?? ''),
        prenom: String(membre?.prenom ?? membre?.Prenom ?? ''),
        idStructure: String(membre?.idStructure ?? membre?.IdStructure ?? membre?.structureId ?? membre?.StructureId ?? ''),
        dateInscription: String(membre?.dateInscription ?? membre?.DateInscription ?? ''),
        rolePilotage: String(membre?.rolePilotage ?? membre?.RolePilotage ?? 'Utilisateur'),
    };
}

function construireQueryString(filtres = {}) {
    const params = new URLSearchParams();

    Object.entries(filtres).forEach(([cle, valeur]) => {
        if (valeur !== undefined && valeur !== null && String(valeur).trim() !== '') {
            params.append(cle, String(valeur));
        }
    });

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
}

function extraireNomFichier(contentDisposition) {
    if (!contentDisposition) {
        return 'membres.csv';
    }

    const matchUtf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (matchUtf8 && matchUtf8[1]) {
        return decodeURIComponent(matchUtf8[1]);
    }

    const matchStandard = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (matchStandard && matchStandard[1]) {
        return matchStandard[1];
    }

    return 'membres.csv';
}

export default class MembreApi {

    static async obtenirTousLesMembres(filtres: Record<string, unknown> = {}): Promise<List<Membre>> {
        return MembreApi.obtenirToutesLesMembres(filtres);
    }

    static async obtenirMembres(filtres: Record<string, unknown> = {}): Promise<List<Membre>> {
        return MembreApi.obtenirToutesLesMembres(filtres);
    }

    static async obtenirToutesLesMembres(filtres: Record<string, unknown> = {}): Promise<List<Membre>> {
        const queryString = construireQueryString(filtres);

        return await fetch(`${lienMembre}/api/membre/membres${queryString}`, { method: 'GET' })
            .then(membres => List((membres as ReponseMembreDto[]).map(normaliserMembre)));
    }

    static async obtenirMembreCourant(membreId: string): Promise<Membre> {
        const endpoints = [
            `${lienMembre}/api/membre/membre/${membreId}`,
            `${lienMembre}/api/membre/membrecourant/${membreId}`
        ];

        for (const endpoint of endpoints) {
            try {
                const membre = await fetch(endpoint, { method: 'GET' }) as ReponseMembreDto;
                return normaliserMembre(membre);
            } catch (error) {
                // Essaie l'endpoint suivant.
            }
        }

        throw new Error('Recuperation du membre impossible');
    }

    static async obtenirMembre(membreId: string): Promise<Membre> {
        return MembreApi.obtenirMembreCourant(membreId);
    }

    static async exporterMembresExcel(filtres: Record<string, unknown> = {}): Promise<{ blob: Blob; nomFichier: string }> {
        const queryString = construireQueryString(filtres);

        const endpoints = [
            `${lienMembre}/api/membre/membres/exporter${queryString}`,
            `${lienMembre}/api/membre/membres/export/excel${queryString}`,
            `${lienMembre}/api/membre/membres/export/xlsx${queryString}`,
            `${lienMembre}/api/membre/membres/export${queryString}`
        ];

        for (const endpoint of endpoints) {
            try {
                const reponse = await window.fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        accept: 'text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream'
                    },
                    credentials: 'omit'
                });

                if (!reponse.ok) {
                    continue;
                }

                const blob = await reponse.blob();
                const nomFichier = extraireNomFichier(reponse.headers.get('content-disposition'));

                return { blob, nomFichier };
            } catch (error) {
                // Essaie l'endpoint suivant.
            }
        }

        throw new Error('Export backend indisponible');
    }

    static async ajouterMembre(membre: Record<string, unknown>): Promise<void> {
        const payload = normaliserPayloadMembre(membre);
        await fetch(`${lienMembre}/api/membre/ajouter`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(payload)
        });
    }

    static async ajouter(membre: Record<string, unknown>): Promise<void> {
        return MembreApi.ajouterMembre(membre);
    }

    static async modifierMembre(id: string, membre: Record<string, unknown>): Promise<void> {
        const payload: MembreDto = { ...normaliserPayloadMembre(membre), id };
        await fetch(`${lienMembre}/api/membre/membre`, {
            method: 'PUT',
            headers: jsonHeaders,
            body: JSON.stringify(payload)
        });
    }

    static async modifier(id: string, membre: Record<string, unknown>): Promise<void> {
        return MembreApi.modifierMembre(id, membre);
    }

    static async supprimerMembre(id: string): Promise<void> {
        const endpoints = [
            `${lienMembre}/api/membre/membre/${id}`,
            `${lienMembre}/api/membre/supprimer/${id}`
        ];

        for (const endpoint of endpoints) {
            try {
                await fetch(endpoint, {
                    method: 'DELETE',
                    headers: jsonHeaders,
                });
                return;
            } catch (error) {
                // Essaie l'endpoint suivant.
            }
        }

        throw new Error('Suppression du membre impossible');
    }

    static async supprimer(id: string): Promise<void> {
        return MembreApi.supprimerMembre(id);
    }
}